import {formatReferenceCopy} from 'utils/typography';
import React, { useEffect, useMemo } from "react";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ExpandMore as ExpandMoreIcon } from '@vedmarket/ui-kit';
import AccordionDetails from "@mui/material/AccordionDetails";
import Button from "@mui/material/Button";
import { LinkPopover, Search as SearchIcon } from '@vedmarket/ui-kit';
import { TurnedIn as TurnedInIcon } from '@vedmarket/ui-kit';

import {COLORS} from "../../../../../../../config/constants";
import CompanyCard from "../company-card";
import {
  useAdditionalServices, useExtraServicesId, useRemoveExtraServicesByOrderIdMutation,
  useSetCompletedByOrderIdMutation, useSummaryCompleted,
  useUserExtraServices
} from "../../../../../../../api/calc";
import {isCalculationViewModeError, numberWithSpaces} from "../../../../../../../utils/helpers";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';
import {useDemoAdditionalServices} from "components/cabinet/demo/steps/additional-services/use-additional-services";

import './additional-services.scss';

type Props = {
  handleOpenModal(): void;
  readOnly?: boolean;
  refetchGetExtraServices(): void;
  setInitialValuesExtraServices(values: number[]): void;
  setSaveStepIsActive(isActive: boolean): void;
}

const AdditionalServices = ({
  handleOpenModal,
  readOnly = false,
  refetchGetExtraServices,
  setInitialValuesExtraServices,
  setSaveStepIsActive,
}: Props) => {
  const [ hasSkipped, setHasSkipped ] = React.useState(false);

  const [ removeExtraServices ] = useRemoveExtraServicesByOrderIdMutation();
  const [ setCompleted ] = useSetCompletedByOrderIdMutation();

  const userExtraServices = useUserExtraServices();
  const additionalServices = useAdditionalServices();
  const userServices = useExtraServicesId() ?? [];
  const orderId = useGetCalcIdFromRoute();
  const summaryCompleted = useSummaryCompleted();
  const { t } = useTranslation();

  const hasSelected = userServices.length > 0;
  const hasDemoScenarioServices = Boolean(additionalServices?.length)
  const shouldShowCompanyCards = Boolean(userExtraServices?.length);
  const demoPlayback = useDemoAdditionalServices(hasDemoScenarioServices)

  useEffect(() => {
    if (summaryCompleted?.additional_services && !hasSelected) {
      setHasSkipped(true);
      return;
    }

    if (hasSelected) {
      setHasSkipped(false);
    }
  }, [ summaryCompleted?.additional_services, hasSelected ]);

  useEffect(() => {
    setSaveStepIsActive(hasSelected || hasSkipped);
  }, [ hasSelected, hasSkipped, setSaveStepIsActive ]);

  const totalServicesCount = userExtraServices?.reduce(
    (sum, company) => sum + company.services.length,
    0
  );

  const totalCost = useMemo(() => {
    return (userExtraServices || []).reduce(
      (acc, item) => acc + (item?.cost_total ?? 0),
      0
    );
  }, [ userExtraServices ]);

  const handleSkipButtonClick = async () => {
    if (hasSelected && userExtraServices?.length) {
      const results = await Promise.all(
        userExtraServices.map((item) =>
          removeExtraServices({
            orderId,
            company_id: item.id,
          })
        )
      );

      if (results.some(isCalculationViewModeError)) {
        return;
      }

      await refetchGetExtraServices?.();

      const completedResult = await setCompleted({
        orderId,
        additional_services: false,
      });

      if (isCalculationViewModeError(completedResult)) {
        return;
      }

      setInitialValuesExtraServices([]);
    }

    setHasSkipped((prev) => !prev);
  };

  const stepDone = hasSelected || hasSkipped;

  return (
    <div className='additional'>
      <Accordion
        {...demoPlayback.accordionProps}
        sx={{
          border: `1px solid ${COLORS.SLIGHTLY_GRAY}`,
          boxShadow: 'none',
          '&.Mui-expanded': {
            marginBottom: 0,
          },
        }}
        defaultExpanded
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon style={{
            width: '24px',
            height: '24px',
          }}/>}
          sx={{
            padding: '24px',
            minHeight: '74px',
            '&.Mui-expanded': {
              minHeight: '74px',
              paddingBottom: '23px',
            },
            '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: COLORS.LIGHT_ACTION,
              marginLeft: '16px',
            },
          }}
        >
          <div className='additional-title'>
            <div className='additional-title__left'>
              <TurnedInIcon style={{
                color: stepDone ? COLORS.GREEN : COLORS.SLIGHTLY_GRAY,
                width: '24px',
                height: '24px',
                flexShrink: 0,
              }}/>
              <Typography variant='h6' sx={{
                color: COLORS.BLACK_DARK,
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: '26px',
                letterSpacing: 0,
              }}>
                {formatReferenceCopy(t('common__text__additionalServices'))}
                {Boolean(totalServicesCount) && (
                  <Typography component='span' variant='h6' sx={{
                    color: COLORS.LIGHT_TEXT_DISABLED_OPACITY,
                    fontSize: '20px',
                    fontWeight: 400,
                    lineHeight: '26px',
                    letterSpacing: 0,
                  }}> {totalServicesCount}</Typography>
                )}
              </Typography>
            </div>
            <div className='additional-title__right'>
              <Typography variant='h6' sx={{
                color: COLORS.BLACK_DARK,
                fontSize: '20px',
                fontWeight: 400,
                lineHeight: '26px',
                letterSpacing: 0,
              }}>
                {`${totalCost ? numberWithSpaces(totalCost) : '0'} USD`}
              </Typography>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={{padding: '24px'}}>
          {shouldShowCompanyCards &&
                userExtraServices?.map((userExtraService) => (
                  <CompanyCard
                    isActive
                    company={userExtraService}
                    key={userExtraService.id}
                    readOnly={readOnly}
                    refetchGetExtraServices={refetchGetExtraServices}
                    setInitialValuesExtraServices={setInitialValuesExtraServices}
                  />
                ))}

          <Button
            {...demoPlayback.findButtonProps}
            variant='outlined'
            className={cx({ 'read-only-action-button': readOnly })}
            startIcon={<SearchIcon />}
            endIcon={readOnlyButtonEndIcon(readOnly)}
            fullWidth
            onClick={readOnly ? undefined : handleOpenModal}
            disabled={readOnly || hasSkipped}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
          >
            <Typography component='span' variant='inherit'>
              {formatReferenceCopy(shouldShowCompanyCards ? t('calc__text__additional__findOtherAdditionalServices') : t('calc__text__additional__findAdditionalServices'))}
            </Typography>
          </Button>

          <FormControlLabel
            control={<Checkbox
              {...demoPlayback.skipButtonProps}
              onChange={readOnly ? undefined : handleSkipButtonClick}
              checked={hasSkipped}
              disabled={readOnly}
            />}
            label={
              <Typography variant='body1' sx={{
                color: COLORS.BLACK_DARK,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: 0,
              }}>
                {formatReferenceCopy(hasSelected ? t('calc__text__additional__removeAdditionalServicesAndSkipSearchForAdditionalServices') : t('calc__text__additional__skipSearchForAdditionalServices'))}
              </Typography>
            }
            sx={{
              margin: 0,
              marginTop: '24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              opacity: readOnly ? 0.6 : 1,
              '& .MuiCheckbox-root': {
                padding: 0,
                flexShrink: 0,
              },
              '& .MuiFormControlLabel-label': {
                minWidth: 0,
              },
            }}
          />
          <div className='additional-services-help'>
            <LinkPopover
              linkText={t('calc__help__additionalServicesLink')}
              text={<div className='additional-services-help__content'>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__additionalServicesIntro'))}
                </Typography>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__additionalServicesChoice'))}
                </Typography>
              </div>}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default AdditionalServices;
